import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { cardBackground, cardBorder, cardShadow } from '../styles/themeHelpers';
import ModernBackground from '../components/common/ModernBackground';
import StarRating from '../components/common/StarRating';
import ReviewDetailModal from '../components/common/ReviewDetailModal';
import ReviewModal from '../components/common/ReviewModal';
import reviewService from '../services/reviewService';
import { getPublicFieldById, getPublicFieldMapById } from '../services/fieldService';
import { getMissionById } from '../services/adminService';
import refereeService from '../services/refereeService';
import { missionDetailService } from '../services/missionDetailService';
import { Review, ReviewSummary, ReviewTargetTypeValue, ReviewFilterRequest } from '../types/review';
import { useAuth } from '../contexts/AuthContext';

const Background = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors?.background};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 40px 20px;
  position: relative;
  z-index: 2;
  color: ${({ theme }) => theme.colors?.text};
`;

const BackButton = styled.button`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid ${({ theme }) => theme.colors?.primary || '#00d4ff'};
  color: ${({ theme }) => theme.colors?.primary || '#00d4ff'};
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-bottom: 24px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    transform: translateY(-2px);
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  padding: 24px;
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 16px;
  box-shadow: ${({ theme }) => cardShadow(theme)};
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors?.primary || '#00d4ff'};
  margin: 0;
  font-size: 1.8rem;
  font-weight: bold;
`;

const TargetInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TargetLink = styled(Link)`
  color: ${({ theme }) => theme.colors?.text};
  text-decoration: none;
  font-size: 1rem;

  &:hover {
    color: ${({ theme }) => theme.colors?.primary || '#00d4ff'};
    text-decoration: underline;
  }
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px;
  background: ${({ theme }) => theme.colors?.surface || 'rgba(0,20,40,0.6)'};
  border: 1px solid ${({ theme }) => theme.colors?.borderLight || 'rgba(0,212,255,0.2)'};
  border-radius: 12px;
  flex-wrap: wrap;
`;

const RatingBig = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const RatingNumber = styled.span`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors?.primary || '#00d4ff'};
`;

const ReviewCount = styled.span`
  color: ${({ theme }) => theme.colors?.textSecondary || '#a0a9b8'};
  font-size: 0.9rem;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors?.surface || 'rgba(0,20,40,0.6)'};
  border: 1px solid ${({ theme }) => theme.colors?.borderLight || 'rgba(0,212,255,0.3)'};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors?.text};
  font-size: 0.9rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors?.primary || '#00d4ff'};
  }
`;

const WriteReviewButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: 1px solid #00d4ff;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  margin-left: auto;

  &:hover {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReviewCard = styled.div`
  padding: 20px;
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors?.primary || '#00d4ff'};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 12px;
`;

const ReviewAuthor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AuthorName = styled.span`
  color: ${({ theme }) => theme.colors?.text};
  font-weight: 600;
  font-size: 1rem;
`;

const ReviewDate = styled.span`
  color: ${({ theme }) => theme.colors?.textSecondary || '#a0a9b8'};
  font-size: 0.8rem;
`;

const VerifiedBadge = styled.span`
  background: rgba(0, 255, 100, 0.1);
  border: 1px solid rgba(0, 255, 100, 0.3);
  color: #00ff64;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ReviewText = styled.p`
  color: ${({ theme }) => theme.colors?.text};
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MediaIndicators = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 12px;
`;

const MediaIndicator = styled.span`
  color: ${({ theme }) => theme.colors?.textSecondary || '#a0a9b8'};
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  background: ${({ $active }) => $active ? 'linear-gradient(135deg, #00d4ff, #0099cc)' : 'rgba(0,20,40,0.6)'};
  border: 1px solid ${({ $active, theme }) => $active ? '#00d4ff' : theme.colors?.borderLight || 'rgba(0,212,255,0.3)'};
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    border-color: #00d4ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 5px solid rgba(0, 212, 255, 0.3);
    border-top: 5px solid #00d4ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
  font-style: italic;
  padding: 60px 20px;
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
`;

const getTargetTypeLabel = (type: string): string => {
  switch (type) {
    case 'field': return 'Field';
    case 'map': return 'Map';
    case 'mission': return 'Mission';
    case 'referee': return 'Referee';
    case 'game': return 'Mission';
    default: return type;
  }
};

const getTargetLink = (type: string, id: string): string => {
  switch (type) {
    case 'field': return `/fields/${id}`;
    case 'map': return `/fields/maps/${id}`;
    case 'mission': return `/operations/${id}`;
    case 'referee': return `/referees/${id}`;
    case 'game': return `/missions/${id}`;
    default: return '#';
  }
};

const ReviewsPage: React.FC = () => {
  const { targetType, targetId } = useParams<{ targetType: string; targetId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isWriteModalOpen, setIsWriteModalOpen] = useState(false);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [targetName, setTargetName] = useState('');

  const validTargetType = targetType as ReviewTargetTypeValue;

  const fetchTargetName = useCallback(async () => {
    if (!targetType || !targetId) return;

    try {
      switch (targetType) {
        case 'field': {
          const field = await getPublicFieldById(targetId);
          setTargetName(field.name);
          break;
        }
        case 'map': {
          const map = await getPublicFieldMapById(targetId);
          // Get field name too
          const field = await getPublicFieldById(map.fieldId);
          setTargetName(`${field.name} - ${map.name}`);
          break;
        }
        case 'mission': {
          const mission = await getMissionById(targetId);
          setTargetName(mission.title || mission.name);
          break;
        }
        case 'referee': {
          const referee = await refereeService.getRefereeProfile(targetId);
          setTargetName(`${referee.firstName} ${referee.lastName}`);
          break;
        }
        case 'game': {
          // Try to get mission detail to show uniqGameId
          try {
            const mission = await missionDetailService.getAirsoftMissionDetail(targetId);
            setTargetName(`Mission ${mission.uniqGameId}`);
          } catch {
            // Try paintball if airsoft fails
            try {
              const mission = await missionDetailService.getPaintballMissionDetail(targetId);
              setTargetName(`Mission ${mission.uniqGameId}`);
            } catch {
              setTargetName('Mission');
            }
          }
          break;
        }
        default:
          setTargetName(getTargetTypeLabel(targetType));
      }
    } catch (error) {
      console.error('Failed to fetch target name:', error);
      setTargetName(getTargetTypeLabel(targetType));
    }
  }, [targetType, targetId]);

  const loadReviews = useCallback(async () => {
    if (!targetType || !targetId) return;

    setLoading(true);
    try {
      const filter: ReviewFilterRequest = {
        page,
        pageSize: 10,
        sortBy,
        sortOrder
      };

      const [reviewsData, summaryData, myReviewData] = await Promise.all([
        reviewService.getReviews(validTargetType, targetId, filter),
        reviewService.getReviewSummary(validTargetType, targetId),
        user ? reviewService.getMyReview(validTargetType, targetId) : Promise.resolve(null)
      ]);

      setReviews(reviewsData.reviews);
      setSummary(summaryData);
      setTotalPages(reviewsData.totalPages);
      setMyReview(myReviewData);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId, page, sortBy, sortOrder, user, validTargetType]);

  useEffect(() => {
    fetchTargetName();
  }, [fetchTargetName]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleReviewClick = (review: Review) => {
    setSelectedReview(review);
    setIsDetailModalOpen(true);
  };

  const handleReviewSubmitted = () => {
    setIsWriteModalOpen(false);
    loadReviews();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!targetType || !targetId) {
    return (
      <Background>
        <ModernBackground />
        <Container>
          <EmptyState>Invalid review page parameters</EmptyState>
        </Container>
      </Background>
    );
  }

  return (
    <Background>
      <ModernBackground />
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          ← Back
        </BackButton>

        <Header>
          <Title>{targetName ? `${targetName} - Reviews` : 'Reviews'}</Title>

          {summary && (
            <SummaryCard>
              <RatingBig>
                <RatingNumber>{summary.rating.toFixed(1)}</RatingNumber>
                <StarRating rating={summary.rating} size="medium" showRating={false} />
                <ReviewCount>{summary.reviewCount} review{summary.reviewCount !== 1 ? 's' : ''}</ReviewCount>
              </RatingBig>
              {summary.reviewCountConfirmed > 0 && (
                <div>
                  <ReviewCount>
                    {summary.reviewCountConfirmed} verified ({summary.ratingConfirmed.toFixed(1)} avg)
                  </ReviewCount>
                </div>
              )}
            </SummaryCard>
          )}
        </Header>

        <FiltersRow>
          <FilterSelect value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
            <option value="date">Sort by Date</option>
            <option value="rating">Sort by Rating</option>
          </FilterSelect>
          <FilterSelect value={sortOrder} onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}>
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </FilterSelect>
          {user && (
            <WriteReviewButton onClick={() => setIsWriteModalOpen(true)}>
              {myReview ? 'Edit Review' : 'Write Review'}
            </WriteReviewButton>
          )}
        </FiltersRow>

        {loading ? (
          <LoadingSpinner />
        ) : reviews.length === 0 ? (
          <EmptyState>
            No reviews yet. Be the first to leave a review!
          </EmptyState>
        ) : (
          <>
            <ReviewsList>
              {reviews.map((review) => (
                <ReviewCard key={review.id} onClick={() => handleReviewClick(review)}>
                  <ReviewHeader>
                    <ReviewAuthor>
                      <AuthorName>{review.userName}</AuthorName>
                      <ReviewDate>{formatDate(review.createdAt)}</ReviewDate>
                    </ReviewAuthor>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {review.isVerified && <VerifiedBadge>Verified</VerifiedBadge>}
                      <StarRating rating={review.rating} size="small" showRating={true} />
                    </div>
                  </ReviewHeader>
                  {review.reviewText && <ReviewText>{review.reviewText}</ReviewText>}
                  {(review.images?.length > 0 || review.youtubeVideoUrl) && (
                    <MediaIndicators>
                      {review.images?.length > 0 && (
                        <MediaIndicator>📷 {review.images.length} photo{review.images.length !== 1 ? 's' : ''}</MediaIndicator>
                      )}
                      {review.youtubeVideoUrl && (
                        <MediaIndicator>🎬 Video</MediaIndicator>
                      )}
                    </MediaIndicators>
                  )}
                </ReviewCard>
              ))}
            </ReviewsList>

            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </PageButton>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <PageButton
                      key={pageNum}
                      $active={pageNum === page}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PageButton>
                  );
                })}
                <PageButton
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </PageButton>
              </Pagination>
            )}
          </>
        )}

        {/* Review Detail Modal */}
        <ReviewDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          review={selectedReview}
        />

        {/* Write/Edit Review Modal */}
        <ReviewModal
          isOpen={isWriteModalOpen}
          onClose={() => setIsWriteModalOpen(false)}
          targetType={validTargetType}
          targetId={targetId}
          targetName={targetName}
          existingReview={myReview}
          onReviewSubmitted={handleReviewSubmitted}
        />
      </Container>
    </Background>
  );
};

export default ReviewsPage;
